import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getFilterStateWithAppliedFilter,
  getInvertedFilter,
  filterDefaultState,
  mapPersonElementToRow,
} from './elementStatusV2Utils';
import {
  createElementStatusV2FilterService,
  deleteElementStatusV2FilterService,
  getElementsStatusV2Service,
  getElementStatusV2FiltersResultCountService,
  getElementStatusV2UserFiltersService,
  setElementStatusV2DefaultFilterService,
  updateElementStatusV2FilterService,
} from '../../../services/elementStatusV2Service';
import {
  CHAMP_ID,
  DELETED_GROUP_ID,
  EMPTY_ID,
  NONE_ID,
  SELF_STUDY_ID,
} from '../../../constants';
import {
  getAllCategoriesService,
  getAllAreaIdsService,
  getAllElementIdsService,
  getAllRoleIdsService,
} from '../../../services/contentService';
import { getAllGroupsService } from '../../../services/groupService';
import { getAllOrganisationUnitIdsService } from '../../../services/organisationService';
import {
  getAllPersonIdsService,
  getWildcardPersonsService,
} from '../../../services/personsService';
import { updateMenteesElementMediatorsService } from '../../../services/roleService';
import { registerBulkTrainingService } from '../../../services/trainingService';

const defaultSorting = {
  columnId: 'elementName',
  direction: 'asc',
};

const BULK_COMPLETION_LIMIT = 5000;

const BULK_REGISTRATION_TYPES = {
  ADMIN_APPROVAL: 'AdminApproval',
  ADMIN_RESET_STATUS: 'AdminResetStatus',
  RESET_STATUS: 'ResetStatus',
  STANDARD: 'Standard',
};

const emptyFilterCatalogs = {
  areaIds: [],
  groupIds: [],
  knowledgeElementIds: [],
  mentorIds: [],
  organisationUnitIds: [],
  personIds: [],
  roleIds: [],
  trainerIds: [],
  wildcardPersonIds: [],
};

const emptyCatalogs = {
  categories: {},
  groups: {},
  knowledgeAreas: {},
  knowledgeElements: {},
  organisationUnitRootNodes: {},
  organisationUnits: {},
  persons: {},
  roles: {},
  wildcardPersons: {},
};

const getFilterCatalogs = ({
  areaIds = [],
  groupIds = [],
  knowledgeElementIds = [],
  organisationUnitIds = [],
  personIds = [],
  roleIds = [],
  wildcardPersonIds = [],
} = {}) => {
  const peopleLikeIds = [...groupIds, ...wildcardPersonIds, ...personIds];
  const trainerIds = [
    EMPTY_ID,
    ...peopleLikeIds.filter(
      (id) => ![SELF_STUDY_ID, DELETED_GROUP_ID, CHAMP_ID].includes(id)
    ),
  ];
  const mentorIds = [
    EMPTY_ID,
    NONE_ID,
    ...peopleLikeIds.filter((id) => ![EMPTY_ID, NONE_ID].includes(id)),
  ];

  return {
    areaIds,
    groupIds,
    knowledgeElementIds,
    mentorIds,
    organisationUnitIds,
    personIds,
    roleIds,
    trainerIds,
    wildcardPersonIds,
  };
};

const getResponseData = (response) => response?.data || response || {};
const getApiRows = (data) => data.rows || Object.values(data.elements || {});
const mapApiRows = (rows = []) => rows.map(mapPersonElementToRow);
const normalizeDefaultFilterId = (id) => (id && id !== EMPTY_ID ? id : null);
const buildSelectedRowsFromRows = (rows = []) =>
  rows.reduce((nextSelectedRows, row) => {
    nextSelectedRows[row.personId] = [
      ...(nextSelectedRows[row.personId] || []),
      row.elementId,
    ];
    return nextSelectedRows;
  }, {});

const getDefaultFilter = (userFilters) => {
  const defaultFilterId = normalizeDefaultFilterId(
    userFilters?.elementStatusDefaultFilterId
  );

  if (!defaultFilterId) {
    return null;
  }

  return (
    userFilters?.elementStatusDefaultFilters?.[defaultFilterId] ||
    userFilters?.presetFilters?.[defaultFilterId] ||
    null
  );
};

const getServerFilterPayload = (filterState, filterCatalogs) =>
  getInvertedFilter(filterState, {
    areaIds: filterCatalogs.areaIds || [],
    knowledgeElementIds: filterCatalogs.knowledgeElementIds || [],
    mentorIds: filterCatalogs.mentorIds || [],
    organisationUnitIds: filterCatalogs.organisationUnitIds || [],
    personIds: filterCatalogs.personIds || [],
    roleIds: filterCatalogs.roleIds || [],
    trainerIds: filterCatalogs.trainerIds || [],
  });

export function useElementStatusV2({ initialFilter = null } = {}) {
  const [activeFilterId, setActiveFilterId] = useState(null);
  const [bulkActionError, setBulkActionError] = useState(null);
  const [filters, setFilterState] = useState(filterDefaultState);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [totalElements, setTotalElements] = useState(null);
  const [defaultFilters, setDefaultFilters] = useState({});
  const [catalogs, setCatalogs] = useState(emptyCatalogs);
  const [filterIds, setFilterIds] = useState(emptyFilterCatalogs);
  const [presetFilters, setPresetFilters] = useState({});
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});
  const [selectedPersonElements, setSelectedPersonElements] = useState([]);
  const [sorting, setSortingState] = useState(defaultSorting);
  const [defaultFilterId, setDefaultFilterId] = useState(null);
  const filterCatalogsRef = useRef(emptyFilterCatalogs);
  const initialServerFilterRef = useRef(null);
  const latestReplaceRequestIdRef = useRef(0);

  const fetchPage = useCallback(
    async ({ cursor = null, filtersArg, replaceRows, sortingArg }) => {
      const requestId = replaceRows
        ? latestReplaceRequestIdRef.current + 1
        : null;

      if (replaceRows) {
        latestReplaceRequestIdRef.current = requestId;
      }

      const response = await getElementsStatusV2Service({
        consistency: 'Eventual',
        cursor,
        filters: filtersArg,
        pageSize: 100,
        sorting: sortingArg,
      });
      const data = getResponseData(response);

      if (replaceRows && latestReplaceRequestIdRef.current !== requestId) {
        return;
      }

      const mappedRows = mapApiRows(getApiRows(data));

      setNextCursor(data.nextCursor || null);
      setTotalElements(data.totalElements ?? null);
      setRows((currentRows) =>
        replaceRows ? mappedRows : [...currentRows, ...mappedRows]
      );

      if (replaceRows) {
        setBulkActionError(null);
      }
    },
    []
  );

  const initializeFilterCatalogs = useCallback(async () => {
    const [
      roleIdsResponse,
      areaIdsResponse,
      elementIdsResponse,
      organisationUnitIdsResponse,
      personIdsResponse,
      groupsResponse,
      wildcardPersonsResponse,
    ] = await Promise.all([
      getAllRoleIdsService(),
      getAllAreaIdsService(),
      getAllElementIdsService(),
      getAllOrganisationUnitIdsService(),
      getAllPersonIdsService(),
      getAllGroupsService(),
      getWildcardPersonsService(),
    ]);

    const groups = getResponseData(groupsResponse);
    const wildcardPersons = getResponseData(wildcardPersonsResponse);

    filterCatalogsRef.current = {
      ...emptyFilterCatalogs,
      ...getFilterCatalogs({
        areaIds: getResponseData(areaIdsResponse),
        groupIds: Object.keys(groups || {}),
        knowledgeElementIds: getResponseData(elementIdsResponse),
        organisationUnitIds: getResponseData(organisationUnitIdsResponse),
        personIds: getResponseData(personIdsResponse),
        roleIds: getResponseData(roleIdsResponse),
        wildcardPersonIds: Object.keys(wildcardPersons || {}),
      }),
    };
    setFilterIds(filterCatalogsRef.current);

    setCatalogs({
      ...emptyCatalogs,
      categories: getResponseData(await getAllCategoriesService()),
      groups,
      wildcardPersons,
    });
  }, []);

  const refreshFilterCounts = useCallback(
    async ({ nextDefaultFilters, nextPresetFilters }) => {
      const response = await getElementStatusV2FiltersResultCountService();
      const filterRowCounts = getResponseData(response).filterRowCounts || {};

      setPresetFilters(
        Object.keys(nextPresetFilters || {}).reduce((accumulator, id) => {
          accumulator[id] = {
            ...(nextPresetFilters || {})[id],
            resultCount:
              filterRowCounts[id] !== undefined
                ? filterRowCounts[id]
                : (nextPresetFilters || {})[id].resultCount,
          };
          return accumulator;
        }, {})
      );

      setDefaultFilters(
        Object.keys(nextDefaultFilters || {}).reduce((accumulator, id) => {
          accumulator[id] = {
            ...(nextDefaultFilters || {})[id],
            resultCount:
              filterRowCounts[id] !== undefined
                ? filterRowCounts[id]
                : (nextDefaultFilters || {})[id].resultCount,
          };
          return accumulator;
        }, {})
      );
    },
    []
  );

  const refreshSavedFilters = useCallback(async () => {
    const userFiltersResponse = await getElementStatusV2UserFiltersService();
    const userFilters = getResponseData(userFiltersResponse);
    const nextPresetFilters = userFilters.presetFilters || {};
    const nextDefaultFilters = userFilters.elementStatusDefaultFilters || {};

    setDefaultFilterId(
      normalizeDefaultFilterId(userFilters.elementStatusDefaultFilterId)
    );
    await refreshFilterCounts({
      nextDefaultFilters,
      nextPresetFilters,
    });

    return userFilters;
  }, [refreshFilterCounts]);

  const buildSavedFilterPayload = useCallback(
    ({ name }) => ({
      ...getServerFilterPayload(filters, filterCatalogsRef.current),
      name,
      sorting,
    }),
    [filters, sorting]
  );

  const getSelectedRowCount = useCallback(
    () =>
      Object.values(selectedRows).reduce(
        (count, selectedElementIds) => count + selectedElementIds.length,
        0
      ),
    [selectedRows]
  );

  const getSelectedPersonElements = useCallback(() => {
    if (selectedPersonElements.length > 0) {
      return selectedPersonElements;
    }

    const selectedRowIds = new Set(
      Object.entries(selectedRows).flatMap(([personId, elementIds]) =>
        elementIds.map((elementId) => `${personId}:${elementId}`)
      )
    );

    return rows.filter((row) =>
      selectedRowIds.has(`${row.personId}:${row.elementId}`)
    );
  }, [rows, selectedPersonElements, selectedRows]);

  const refreshRowsForPersonElements = useCallback(
    async (personElements) => {
      if (personElements.length === 0) {
        return [];
      }

      const personIds = [...new Set(personElements.map((row) => row.personId))];
      const knowledgeElementIds = [
        ...new Set(personElements.map((row) => row.elementId)),
      ];
      const response = await getElementsStatusV2Service({
        consistency: 'Strong',
        cursor: null,
        filters: {
          knowledgeElements: knowledgeElementIds,
          persons: personIds,
        },
        pageSize: knowledgeElementIds.length || 100,
        sorting,
      });
      const data = getResponseData(response);
      const refreshedRows = mapApiRows(getApiRows(data));

      setRows((currentRows) => {
        const refreshedRowsById = refreshedRows.reduce((accumulator, row) => {
          accumulator[`${row.personId}:${row.elementId}`] = row;
          return accumulator;
        }, {});

        return currentRows.map((row) => {
          const key = `${row.personId}:${row.elementId}`;
          return refreshedRowsById[key] || row;
        });
      });
      await refreshFilterCounts({
        nextDefaultFilters: defaultFilters,
        nextPresetFilters: presetFilters,
      });
      return refreshedRows;
    },
    [defaultFilters, presetFilters, refreshFilterCounts, sorting]
  );

  const guardBulkLimit = useCallback(() => {
    if (getSelectedRowCount() > BULK_COMPLETION_LIMIT) {
      setBulkActionError('Bulk action limit exceeded');
      return false;
    }

    setBulkActionError(null);
    return true;
  }, [getSelectedRowCount]);

  const clearBulkActionError = useCallback(() => {
    setBulkActionError(null);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadInitialPage = async () => {
      try {
        setHasError(false);

        const [userFiltersResponse] = await Promise.all([
          getElementStatusV2UserFiltersService(),
          initializeFilterCatalogs(),
        ]);
        const userFilters = getResponseData(userFiltersResponse);
        const defaultFilter = getDefaultFilter(userFilters);
        const effectiveInitialFilter = initialFilter || defaultFilter;
        const { sorting: effectiveInitialSorting, ...effectiveServerFilters } =
          effectiveInitialFilter || {};
        setDefaultFilterId(
          normalizeDefaultFilterId(userFilters.elementStatusDefaultFilterId)
        );
        await refreshFilterCounts({
          nextDefaultFilters: userFilters.elementStatusDefaultFilters || {},
          nextPresetFilters: userFilters.presetFilters || {},
        });
        initialServerFilterRef.current = effectiveInitialFilter
          ? effectiveServerFilters
          : null;

        if (effectiveInitialFilter) {
          if (
            filterCatalogsRef.current.personIds?.length > 0 ||
            filterCatalogsRef.current.organisationUnitIds?.length > 0 ||
            filterCatalogsRef.current.roleIds?.length > 0 ||
            filterCatalogsRef.current.areaIds?.length > 0 ||
            filterCatalogsRef.current.knowledgeElementIds?.length > 0 ||
            filterCatalogsRef.current.trainerIds?.length > 0 ||
            filterCatalogsRef.current.mentorIds?.length > 0
          ) {
            setFilterState(
              getFilterStateWithAppliedFilter({
                areaIds: filterCatalogsRef.current.areaIds || [],
                filter: effectiveServerFilters,
                groupIds: filterCatalogsRef.current.groupIds || [],
                knowledgeElementIds:
                  filterCatalogsRef.current.knowledgeElementIds || [],
                organisationUnitIds:
                  filterCatalogsRef.current.organisationUnitIds || [],
                personIds: filterCatalogsRef.current.personIds || [],
                roleIds: filterCatalogsRef.current.roleIds || [],
                mentorIds: filterCatalogsRef.current.mentorIds || [],
                trainerIds: filterCatalogsRef.current.trainerIds || [],
                wildcardPersonIds:
                  filterCatalogsRef.current.wildcardPersonIds || [],
              })
            );
          }

          if (effectiveInitialSorting) {
            setSortingState(effectiveInitialSorting);
          }
        }

        await fetchPage({
          cursor: null,
          filtersArg: effectiveInitialFilter
            ? effectiveServerFilters
            : getServerFilterPayload(filterDefaultState, emptyFilterCatalogs),
          replaceRows: true,
          sortingArg: effectiveInitialSorting || defaultSorting,
        });
      } catch {
        if (isMounted) {
          setHasError(true);
          setRows([]);
          setNextCursor(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setIsLoadingInitial(false);
        }
      }
    };

    loadInitialPage();

    return () => {
      isMounted = false;
    };
  }, [fetchPage, initialFilter, initializeFilterCatalogs, refreshFilterCounts]);

  const loadMore = useCallback(async () => {
    if (
      !nextCursor ||
      isLoadingMore ||
      (totalElements !== null && rows.length >= totalElements)
    ) {
      return;
    }

    setIsLoadingMore(true);

    try {
      await fetchPage({
        cursor: nextCursor,
        filtersArg:
          initialServerFilterRef.current ||
          getServerFilterPayload(filters, filterCatalogsRef.current),
        replaceRows: false,
        sortingArg: sorting,
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    fetchPage,
    filters,
    isLoadingMore,
    nextCursor,
    rows.length,
    sorting,
    totalElements,
  ]);

  const toggleRowSelection = useCallback(
    (personId, elementId) => {
      setSelectedRows((previousSelectedRows) => {
        const currentSelection = previousSelectedRows[personId] || [];
        const nextSelection = currentSelection.includes(elementId)
          ? currentSelection.filter((id) => id !== elementId)
          : [...currentSelection, elementId];

        if (nextSelection.length === 0) {
          const { [personId]: removedPersonId, ...remainingSelections } =
            previousSelectedRows;
          return remainingSelections;
        }

        return {
          ...previousSelectedRows,
          [personId]: nextSelection,
        };
      });

      setSelectedPersonElements((previousSelectedPersonElements) => {
        const existingIndex = previousSelectedPersonElements.findIndex(
          (row) => row.personId === personId && row.elementId === elementId
        );

        if (existingIndex >= 0) {
          return previousSelectedPersonElements.filter(
            (_, index) => index !== existingIndex
          );
        }

        const row = rows.find(
          (currentRow) =>
            currentRow.personId === personId &&
            currentRow.elementId === elementId
        );

        return row
          ? [...previousSelectedPersonElements, row]
          : previousSelectedPersonElements;
      });
    },
    [rows]
  );

  const toggleAllLoadedRows = useCallback(async () => {
    const allLoadedRowsSelected =
      rows.length > 0 &&
      rows.every((row) =>
        (selectedRows[row.personId] || []).includes(row.elementId)
      );

    if (allLoadedRowsSelected) {
      setSelectedRows({});
      setSelectedPersonElements([]);
      setBulkActionError(null);
      return;
    }

    if (totalElements > BULK_COMPLETION_LIMIT) {
      setBulkActionError('Bulk action limit exceeded');
      return;
    }

    if (totalElements !== null && rows.length < totalElements) {
      setIsSelectingAll(true);
      try {
        const response = await getElementsStatusV2Service({
          consistency: 'Eventual',
          cursor: null,
          filters:
            initialServerFilterRef.current ||
            getServerFilterPayload(filters, filterCatalogsRef.current),
          pageSize: totalElements,
          sorting,
        });
        const allRows = mapApiRows(getApiRows(getResponseData(response)));
        const builtSelectedRows = buildSelectedRowsFromRows(allRows);
        const personElements = allRows.reduce((acc, row) => {
          return [
            ...acc,
            {
              elementId: row.elementId,
              elementName: row.elementName,
              personId: row.personId,
              nextAction: row.nextAction,
              pending: row.pending,
              mediatorId: row.responsibleId,
            },
          ];
        }, []);
        setSelectedRows(builtSelectedRows);
        setSelectedPersonElements(personElements);

        setBulkActionError(null);
      } finally {
        setIsSelectingAll(false);
      }
      return;
    }

    setSelectedRows(buildSelectedRowsFromRows(rows));
    setSelectedPersonElements(rows);
    setBulkActionError(null);
  }, [filters, rows, selectedRows, sorting, totalElements]);

  const selectedCount = Object.values(selectedRows).reduce(
    (count, selectedElementIds) => count + selectedElementIds.length,
    0
  );

  const bulkComplete = useCallback(async () => {
    if (!guardBulkLimit()) {
      return;
    }

    const personElements = getSelectedPersonElements();
    await registerBulkTrainingService({
      operations: Object.entries(selectedRows).map(([menteeId, elements]) => ({
        elements,
        menteeId,
      })),
      type: BULK_REGISTRATION_TYPES.STANDARD,
    });
    await refreshRowsForPersonElements(personElements);
  }, [
    getSelectedPersonElements,
    guardBulkLimit,
    refreshRowsForPersonElements,
    selectedRows,
  ]);

  const bulkReset = useCallback(
    async ({ note }) => {
      if (!guardBulkLimit()) {
        return;
      }

      const personElements = getSelectedPersonElements();
      await registerBulkTrainingService({
        note,
        operations: Object.entries(selectedRows).map(
          ([menteeId, elements]) => ({
            elements,
            menteeId,
          })
        ),
        type: BULK_REGISTRATION_TYPES.RESET_STATUS,
      });
      await refreshRowsForPersonElements(personElements);
    },
    [
      getSelectedPersonElements,
      guardBulkLimit,
      refreshRowsForPersonElements,
      selectedRows,
    ]
  );

  const bulkAdminReset = useCallback(
    async ({ note, validFromDate }) => {
      if (!guardBulkLimit()) {
        return;
      }

      const personElements = getSelectedPersonElements();
      await registerBulkTrainingService({
        note,
        operations: Object.entries(selectedRows).map(
          ([menteeId, elements]) => ({
            elements,
            menteeId,
          })
        ),
        type: BULK_REGISTRATION_TYPES.ADMIN_RESET_STATUS,
        validFromDate,
      });
      await refreshRowsForPersonElements(personElements);
    },
    [
      getSelectedPersonElements,
      guardBulkLimit,
      refreshRowsForPersonElements,
      selectedRows,
    ]
  );

  const bulkAdminComplete = useCallback(
    async ({ note, validFromDate }) => {
      if (!guardBulkLimit()) {
        return;
      }

      const personElements = getSelectedPersonElements();
      await registerBulkTrainingService({
        note,
        operations: Object.entries(selectedRows).map(
          ([menteeId, elements]) => ({
            elements,
            menteeId,
          })
        ),
        type: BULK_REGISTRATION_TYPES.ADMIN_APPROVAL,
        validFromDate,
      });
      await refreshRowsForPersonElements(personElements);
    },
    [
      getSelectedPersonElements,
      guardBulkLimit,
      refreshRowsForPersonElements,
      selectedRows,
    ]
  );

  const bulkChangeResponsible = useCallback(
    async ({ mediatorId }) => {
      if (!guardBulkLimit()) {
        return;
      }

      const personElements = getSelectedPersonElements();
      await updateMenteesElementMediatorsService({
        elements: personElements.map((row) => ({
          knowledgeAreaId:
            row.raw?.knowledgeAreaId ||
            row.raw?.knowledgeArea?.id ||
            row.raw?.connections?.knowledgeArea ||
            row.raw?.raw?.knowledgeAreaId ||
            row.raw?.raw?.knowledgeArea?.id ||
            row.raw?.raw?.connections?.knowledgeArea,
          knowledgeElementId: row.elementId,
          mediatorId,
          menteeId: row.personId,
        })),
      });
      await refreshRowsForPersonElements(personElements);
    },
    [getSelectedPersonElements, guardBulkLimit, refreshRowsForPersonElements]
  );

  const applySavedFilter = useCallback(
    async (filter) => {
      if (!filter) {
        return;
      }

      const { sorting: nextSorting = sorting, ...serverFilter } = filter;
      const nextFilters = getFilterStateWithAppliedFilter({
        areaIds: filterCatalogsRef.current.areaIds || [],
        filter: serverFilter,
        groupIds: filterCatalogsRef.current.groupIds || [],
        knowledgeElementIds:
          filterCatalogsRef.current.knowledgeElementIds || [],
        organisationUnitIds:
          filterCatalogsRef.current.organisationUnitIds || [],
        personIds: filterCatalogsRef.current.personIds || [],
        roleIds: filterCatalogsRef.current.roleIds || [],
        mentorIds: filterCatalogsRef.current.mentorIds || [],
        trainerIds: filterCatalogsRef.current.trainerIds || [],
        wildcardPersonIds: filterCatalogsRef.current.wildcardPersonIds || [],
      });

      initialServerFilterRef.current = serverFilter;
      setFilterState(nextFilters);
      setSelectedPersonElements([]);
      setSelectedRows({});
      setSortingState(nextSorting);
      setNextCursor(null);
      setIsLoading(true);
      setHasError(false);
      setActiveFilterId(filter.id || null);

      try {
        await fetchPage({
          cursor: null,
          filtersArg: serverFilter,
          replaceRows: true,
          sortingArg: nextSorting,
        });
      } catch {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPage, sorting]
  );

  const createSavedFilter = useCallback(
    async ({ name }) => {
      await createElementStatusV2FilterService(
        buildSavedFilterPayload({ name })
      );
      await refreshSavedFilters();
    },
    [buildSavedFilterPayload, refreshSavedFilters]
  );

  const updateSavedFilter = useCallback(
    async ({ id, name }) => {
      await updateElementStatusV2FilterService({
        id,
        filter: buildSavedFilterPayload({ name }),
      });
      await refreshSavedFilters();
    },
    [buildSavedFilterPayload, refreshSavedFilters]
  );

  const deleteSavedFilter = useCallback(
    async (id) => {
      await deleteElementStatusV2FilterService(id);
      await refreshSavedFilters();
    },
    [refreshSavedFilters]
  );

  const setDefaultFilter = useCallback(
    async (id) => {
      const nextDefaultFilterId =
        id === null || id === defaultFilterId ? null : id;

      await setElementStatusV2DefaultFilterService(
        nextDefaultFilterId ?? EMPTY_ID
      );
      await refreshSavedFilters();
    },
    [defaultFilterId, refreshSavedFilters]
  );

  const refreshRowStatus = useCallback(
    async (personId, elementId) => {
      const personElements = rows.filter(
        (row) => row.personId === personId && row.elementId === elementId
      );

      await refreshRowsForPersonElements(personElements);
    },
    [refreshRowsForPersonElements, rows]
  );

  const setSorting = useCallback(
    async (nextSorting) => {
      setHasError(false);
      setIsLoading(true);
      setSelectedPersonElements([]);
      setSelectedRows({});
      setSortingState(nextSorting);
      setNextCursor(null);

      try {
        await fetchPage({
          cursor: null,
          filtersArg:
            initialServerFilterRef.current ||
            getServerFilterPayload(filters, filterCatalogsRef.current),
          replaceRows: true,
          sortingArg: nextSorting,
        });
      } catch {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPage, filters]
  );

  const setFilters = useCallback(
    async (nextFilters, nextFilterCatalogs = null) => {
      setHasError(false);
      setIsLoading(true);
      activeFilterId && setActiveFilterId(null);
      if (nextFilterCatalogs) {
        filterCatalogsRef.current = {
          ...filterCatalogsRef.current,
          ...nextFilterCatalogs,
        };
        setFilterIds(filterCatalogsRef.current);
      }
      initialServerFilterRef.current = null;
      setFilterState(nextFilters);
      setSelectedPersonElements([]);
      setSelectedRows({});
      setNextCursor(null);

      try {
        await fetchPage({
          cursor: null,
          filtersArg: getServerFilterPayload(
            nextFilters,
            filterCatalogsRef.current
          ),
          replaceRows: true,
          sortingArg: sorting,
        });
      } catch {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPage, sorting]
  );

  return {
    activeFilterId,
    applySavedFilter,
    bulkActionError,
    bulkAdminComplete,
    bulkAdminReset,
    bulkChangeResponsible,
    bulkComplete,
    bulkReset,
    clearBulkActionError,
    catalogs,
    createSavedFilter,
    defaultFilters,
    defaultFilterId,
    deleteSavedFilter,
    filterIds,
    filters,
    hasError,
    isLoading,
    isLoadingInitial,
    isLoadingMore,
    isSelectingAll,
    loadMore,
    presetFilters,
    refreshRowsForPersonElements,
    rows,
    refreshRowStatus,
    selectedCount,
    selectedRows,
    selectedPersonElements,
    setFilters,
    setDefaultFilter,
    setSorting,
    sorting,
    totalElements,
    toggleAllLoadedRows,
    toggleRowSelection,
    updateSavedFilter,
  };
}
